import Slider from 'rc-slider';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const getOptions = (min: number, max: number, stepSize: number) => {
  const difference = max - min;
  const numberOfSteps = difference / stepSize + 1;

  const options = [];

  for (let i = 0; i < numberOfSteps; i++) {
    options.push(min + stepSize * i);
  }

  return options;
};

interface SelectSliderProsp {
  title: string;
  min: number;
  max: number;
  step: number;
  defaultValue: number;
  current: number;
  isPending: boolean;
  rangeLabel?: {
    min: string | number;
    max: string | number;
    unit: string;
  };
  onChange: (value: number) => void;
}

const SelectSlider = ({
  title,
  min,
  max,
  step,
  defaultValue,
  current,
  isPending,
  rangeLabel,
  onChange,
}: SelectSliderProsp) => {
  const { t } = useTranslation(['finance']);
  const [localValue, setLocalValue] = useState(current);
  const localRefValue = useRef(localValue);
  localRefValue.current = localValue;

  const hasNoRange = min === 0 && max === 0;

  if (hasNoRange) {
    return <div />;
  }

  const options = useMemo(() => getOptions(min, max, step), [min, max, step]);

  const onChangeInput = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = Number.parseInt(e.currentTarget.value, 10);
    setLocalValue(value);
    onChange(value);
  };

  const onChangeSlider = (_value: number | number[]) => {
    const value = Array.isArray(_value) ? _value[0] : _value;
    setLocalValue(value);
  };

  const handleAfterChange = () => {
    onChange(localRefValue.current);
  };

  return (
    <div className="m-t">
      <div data-wx-inputlabel="">{title}</div>
      <div data-am-inputrow="always valign-middle">
        <div className="input-row">
          <div className="input-row-item">
            <div data-wx-select="">
              <select
                className="select"
                value={localValue}
                disabled={isPending}
                onChange={onChangeInput}
              >
                {options.map((option) => (
                  <option key={option} value={option}>
                    {option} {t('monthsUnit', { ns: 'finance' })}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="input-row-item">
            <div data-wx-rangeslider="simple">
              <div className="range-slider">
                <Slider
                  min={min}
                  max={max}
                  step={step}
                  defaultValue={defaultValue}
                  value={localValue}
                  disabled={isPending}
                  onChange={onChangeSlider}
                  onChangeComplete={handleAfterChange}
                />
              </div>
              {rangeLabel !== undefined && (
                <div className="range-span">
                  <div className="range-span-low">
                    {rangeLabel.min}
                    {rangeLabel.unit}
                  </div>
                  <div className="range-span-high">
                    {rangeLabel.max}
                    {rangeLabel.unit}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectSlider;
